import React, { useEffect } from "react";
import { Helmet } from "react-helmet";

import "./AffiliateProgram.css";

// The public recruitment page for the Invoice Chaser affiliate program.
//
// Everyone is on the same deal: 50% of the referral's first monthly payment,
// ex GST, with no discount taken off the referral's price. Deliberately no
// dollar figures on this page — the commission is always expressed as a share.
//
// Styled to match the mploi.com marketing site rather than the older React
// landing pages, so it does not use the shared Header/Footer/Section1
// components. All layout lives in AffiliateProgram.css.

const AFFILIATE_SIGNUP = "/accounts/affiliate";

// Everything that is not the signup form points at the mploi.com marketing
// site, not at this app. core.mploi.com only holds the previous-generation
// landing pages (dark purple), so "/" and "/accounts" would drop a visitor
// onto the old brand mid-journey.
const MPLOI_SITE = "https://mploi.com";
const INVOICE_CHASER = "https://mploi.com/our-services/ai-invoice-chaser";
const PRIVACY_URL = "https://mploi.com/privacy-policy";
const TERMS_URL = "https://mploi.com/terms-conditions";

const WHO_ITS_FOR = [
  {
    title: "Bookkeepers and accountants",
    text: "You already know which of your clients are carrying a debtors ledger they never get to. Invoice Chaser does the calling, you keep the relationship.",
  },
  {
    title: "Industry bodies and associations",
    text: "Late payment is the complaint your members raise every year. Give them something that moves the number, and earn on every one who takes it up.",
  },
  {
    title: "Business coaches and consultants",
    text: "Cash flow is the first thing you fix. Refer the tool, and the commission comes back to you when they subscribe.",
  },
];

const STEPS = [
  {
    pill: "Step one",
    title: "Register as an affiliate",
    text: "A short form: your details, how you plan to promote, and where you want to be paid — PayPal or PayID. You get your own referral code straight away.",
  },
  {
    pill: "Step two",
    title: "Share your link",
    text: "Your code becomes a link you can put in an email, a newsletter, a proposal or a talk. Anyone who signs up through it is tied to you, on any device.",
  },
  {
    pill: "Step three",
    title: "Get paid",
    text: "When your referral's first payment clears, your commission is recorded against your code. You can see every referral and its status in your dashboard.",
  },
];

const TERMS = [
  {
    label: "What you earn",
    value: "50% of your referral's first monthly Invoice Chaser payment, ex GST.",
  },
  {
    label: "What your referral pays",
    value: "The standard price. Your link never changes what they are charged — the commission comes out of our side, not theirs.",
  },
  {
    label: "One-off, not trailing",
    value: "The commission is earned on the first month only. There is no ongoing percentage of later payments.",
  },
  {
    label: "When it is earned",
    value: "The moment their first payment clears. If a referral signs up and never pays, or cancels beforehand, no commission is earned.",
  },
  {
    label: "How you are paid",
    value: "By PayPal or PayID, in a payout run, against a statement showing every referral in the batch. If you are registered for GST, tell us in your settings and it is handled on your statement.",
  },
  {
    label: "What counts as a referral",
    value: "A new Invoice Chaser customer who signed up through your link. Referring yourself, or your own business, does not earn a commission.",
  },
];

export default function AffiliateProgram() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="affPage">
      <Helmet>
        <title>MPLOI Affiliate Program: Earn on Every Invoice Chaser Referral</title>
        <meta
          name="description"
          content="Refer a business to MPLOI Invoice Chaser and earn 50% of their first monthly payment. No cost to join, and your referral pays the standard price."
        />
        <link rel="preconnect" href="https://api.fontshare.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://api.fontshare.com/v2/css?f[]=switzer@400,500,600,700&display=swap"
        />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap"
        />
      </Helmet>

      {/* --- Nav --- */}
      <div className="affNav">
        <div className="affContainer">
          <div className="affNavInner">
            <a href={MPLOI_SITE}>
              <img src="/assets/images/mploi-logo-white.svg" alt="MPLOI" />
            </a>
            <a className="affBtn affBtn--primary" href={AFFILIATE_SIGNUP}>
              Become an Affiliate
            </a>
          </div>
        </div>
      </div>

      {/* --- Hero --- */}
      <div className="affBand">
        <div className="affContainer">
          <p className="affEyebrow">Affiliate Program</p>
          <h1 className="affHeroTitle">
            Get paid for introducing
            <span className="affHeroSerif">Invoice Chaser</span>
          </h1>
          <p className="affHeroLede">
            Every business you know is owed money it is too busy, or too
            uncomfortable, to chase. MPLOI Invoice Chaser makes those calls for
            them. Refer one, and you earn 50% of their first monthly payment.
          </p>
          <div className="affBtnRow">
            <a className="affBtn affBtn--primary" href={AFFILIATE_SIGNUP}>
              Become an Affiliate
            </a>
            <a className="affBtn affBtn--ghost" href="#how-it-works">
              How it works
            </a>
          </div>
        </div>
      </div>

      {/* --- Who it's for --- */}
      <div className="affBand affBand--tint">
        <div className="affContainer">
          <p className="affEyebrow">Who this is for</p>
          <h2 className="affHeading">You do not need to be a salesperson</h2>
          <p className="affLede">
            You need to know business owners who are owed money — and most people
            who work with small business know plenty.
          </p>
          <div className="affGrid">
            {WHO_ITS_FOR.map((item) => (
              <div key={item.title} className="affCard">
                <h3 className="affCardTitle">{item.title}</h3>
                <p className="affCardText">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- How it works --- */}
      <div id="how-it-works" className="affBand">
        <div className="affContainer">
          <p className="affEyebrow">How it works</p>
          <h2 className="affHeading">Three steps, and nothing to pay</h2>
          <p className="affLede">Joining is free.</p>
          <div className="affGrid">
            {STEPS.map((item) => (
              <div key={item.pill} className="affCard">
                <span className="affPill">{item.pill}</span>
                <h3 className="affCardTitle">{item.title}</h3>
                <p className="affCardText">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- What you are referring --- */}
      <div className="affBand affBand--tint">
        <div className="affContainer">
          <p className="affEyebrow">What you are referring</p>
          <p className="affStatement">
            Invoice Chaser is an AI team member that calls a business's overdue
            debtors for them. It works from their accounting software, calls in a
            voice and tone they choose, handles the call back, remembers the last
            conversation, and emails them a transcript of every one.
          </p>
          <a className="affBtn affBtn--ghost" href={INVOICE_CHASER}>
            See Invoice Chaser
          </a>
        </div>
      </div>

      {/* --- The deal --- */}
      <div className="affBand">
        <div className="affContainer">
          <p className="affEyebrow">The deal, in plain terms</p>
          <h2 className="affHeading">One deal for everyone</h2>
          <p className="affLede">No tiers to climb and nothing to negotiate.</p>
          <ul className="affTerms">
            {TERMS.map((item) => (
              <li key={item.label} className="affTermsRow">
                <div className="affTermsLabel">{item.label}</div>
                <div className="affTermsValue">{item.value}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* --- Closing CTA --- */}
      <div className="affBand affBand--dark">
        <div className="affContainer">
          <p className="affEyebrow">Ready to start?</p>
          <h2 className="affHeading">
            Register in a couple of minutes and your referral link is live
            immediately.
          </h2>
          <div className="affBtnRow">
            <a className="affBtn affBtn--primary" href={AFFILIATE_SIGNUP}>
              Become an Affiliate
            </a>
            <a className="affBtn affBtn--ghost" href={INVOICE_CHASER}>
              See Invoice Chaser
            </a>
          </div>
        </div>
      </div>

      {/* --- Footer --- */}
      <div className="affFooter">
        <div className="affContainer affFooterInner">
          <div>© {new Date().getFullYear()} MPLOI</div>
          <div className="affFooterLinks">
            <a href={INVOICE_CHASER}>Invoice Chaser</a>
            <a href={PRIVACY_URL}>Privacy</a>
            <a href={TERMS_URL}>Terms</a>
          </div>
        </div>
      </div>
    </div>
  );
}
